import styled from '@emotion/styled'

import Link from 'next/link'

import type { FC } from 'react'

import Badge from '@segment/matcha/components/Badge'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroRecipeBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import AuthorsBox from './AuthorsBox'
import type { AuthorsBoxProps } from './AuthorsBox'
import RequirementList from './RequirementList'
import Cta from './Cta'

interface HeroProps {
  heading: string
  description: string
  authors?: AuthorsBoxProps[`authors`]
  badges: string[] | undefined
  requirements?: string[]
  ctaAdTitle?: string | undefined
}

const recipesLink = {
  text: `Recipes`,
  href: `/recipes`,
}

const Hero: FC<HeroProps> = ({ heading, description, authors, badges, requirements, ctaAdTitle }) => {
  return (
    <Wrapper>
      <StyledHeroRecipeBase variant="2Col" copyVariant="wide">
        <StyledHeroCopy>
          <Link href={recipesLink.href}>
            <StyledButtonText tag="span" iconStart={ChevronLeftIcon}>
              {recipesLink.text}
            </StyledButtonText>
          </Link>
          {badges && (
            <BadgesWrapper>
              {badges.map((badge) => (
                <Badge variant="outlined" key={badge}>
                  {badge}
                </Badge>
              ))}
            </BadgesWrapper>
          )}
          <CopyTextBase>
            <CopyText.Heading tag="h1" variant="h1">
              {heading}
            </CopyText.Heading>
            <CopyText.Description tag="p" variant="pExtraLarge">
              {description}
            </CopyText.Description>
          </CopyTextBase>
          {authors && authors.length && <AuthorsBox authors={authors} />}
        </StyledHeroCopy>
        {requirements && requirements.length && (
          <StyledHeroContents>
            <RequirementList requirements={requirements} />
            <Cta title={ctaAdTitle} />
          </StyledHeroContents>
        )}
      </StyledHeroRecipeBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroRecipeBase = styled(HeroRecipeBase)`
  ${HeroRecipe.Wrapper} {
    align-items: start;
    ${({ theme }) => theme.breakpoints.up(`md`)} {
      padding-top: 80px;
      padding-bottom: 80px;
    }
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      grid-template-columns: minmax(472px, 60%) minmax(280px, 40%);
    }
    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      grid-template-columns: minmax(612px, 65%) minmax(308px, 35%);
    }
    ${({ theme }) => theme.breakpoints.up(`xxl`)} {
      grid-template-columns: minmax(852px, 60%) minmax(308px, 40%);
    }
  }
`

const StyledHeroCopy = styled(HeroRecipe.Copy)`
  text-align: left;
  display: grid;
  justify-items: start;
  row-gap: 24px;
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    row-gap: 40px;
  }
  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    row-gap: 32px;
  }
`

const StyledButtonText = styled(ButtonText)`
  margin-bottom: 6px;
  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    margin-bottom: 16px;
  }
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-bottom: 0;
  }
  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    margin-bottom: 8px;
  }
`

const StyledHeroContents = styled(HeroRecipe.Contents)`
  text-align: left;
  max-width: 520px;
  display: grid;
  row-gap: 40px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 70px;
  }
`

const BadgesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export default Hero
